/*
  @layer  module
  @name   Facettes — améliorations progressives
  @deps   assets/facets.js (Dawn) pour `price-range` et `FacetFiltersForm` ;
          assets/enet-core-collection.css pour l'habillage
  @i18n   aucune — aucun texte n'est produit ici

  Deux améliorations, aucune réécriture. Le fichier est chargé après `facets.js` et ne fait
  qu'ajouter : sans lui, les facettes de Dawn fonctionnent exactement comme avant.

  1. RECOPIE DE LA PASTILLE DE TRI MOBILE VERS LE `<select>` DE DAWN
  2. CURSEUR À DEUX POIGNÉES PAR-DESSUS LE FILTRE DE PRIX
*/

(() => {
  'use strict';

  /* ------------------------------------------------------------------------
     1. Tri mobile — recopie de valeur

     `snippets/enet-mod-facets-sort.liquid` explique pourquoi la pastille mobile n'est pas un
     formulaire : la liste des formulaires soumis est codée en dur dans Dawn. On recopie donc
     la valeur dans le `<select>` de Dawn et on lui rend la main.

     L'écouteur est délégué au document, et non posé sur le `<select>` : Dawn remplace le
     contenu de `.facets-container` à chaque filtrage, ce qui détruirait un écouteur direct
     sans qu'on le voie — les tris suivants seraient devenus muets.
     ------------------------------------------------------------------------ */

  document.addEventListener('change', (event) => {
    const source = event.target.closest?.('[data-enet-sort-mirror]');
    if (!source) return;

    const target = document.getElementById(source.dataset.enetSortMirror);
    if (!target || target === source) return;

    target.value = source.value;

    // `input` et non `change` : l'écouteur de Dawn est posé sur le formulaire, en `input`.
    // L'évènement doit remonter jusqu'à lui, d'où `bubbles`.
    target.dispatchEvent(new Event('input', { bubbles: true }));
  });

  /* ------------------------------------------------------------------------
     2. Curseur de prix

     La maquette montre un curseur à deux poignées et **rien d'autre** ; Dawn ne fournit que
     deux champs « De » et « À ». Le curseur est ajouté, les champs sont masqués par le CSS.

     LES CHAMPS SONT MASQUÉS, PAS SUPPRIMÉS — ET LE RÔLE ACCESSIBLE SE DÉPLACE AVEC EUX.

     Les champs restent dans le DOM parce qu'ils portent les `name` que Dawn soumet : un
     `display: none` ne les empêche pas d'être envoyés, seul `disabled` le ferait. Mais ils ne
     sont plus ni visibles ni annoncés, donc ils ne peuvent plus porter l'accessibilité du
     filtre. Les deux `<input type="range">` la reprennent : ordre de tabulation normal, nom
     accessible repris du `<label>` de Dawn, et `aria-valuetext` pour annoncer « 8,00 € »
     plutôt que « 8 ». Sans ce transfert, retirer les champs rendrait le filtre de prix
     inutilisable au clavier et muet aux lecteurs d'écran.

     C'est l'inverse de la répartition du lot initial, où les champs étaient visibles et les
     curseurs en `aria-hidden` : deux contrôles exposés pour un filtre, jamais quatre.

     Le repli sans JavaScript et en contrastes forcés reste les champs de Dawn — voir la
     section 4 de `enet-core-collection.css`, qui les réaffiche dans ces deux cas.

     Aucune valeur n'est lue dans un réglage : le maximum, le séparateur décimal, le symbole
     monétaire et le format viennent du balisage que Dawn produit déjà. Le composant suit donc
     la devise et la locale de la boutique sans rien savoir d'elles.
     ------------------------------------------------------------------------ */

  const DECIMALS = 2;

  // « 1 250,00 » → 1250. Les espaces sont des séparateurs de milliers, la virgule un
  // séparateur décimal. On ne peut pas passer par `parseFloat` directement : il s'arrêterait
  // à la virgule et lirait 1 pour « 1,50 ».
  //
  // Les deux échappements Unicode sont l'espace insécable et l'espace insécable étroit, que
  // `money_without_currency` produit selon la locale. Ils sont écrits en `\u` et non en clair :
  // un caractère invisible dans une classe de caractères est indébogable.
  const toNumber = (raw) => {
    if (!raw) return null;
    const normalised = String(raw)
      .replace(/[\s\u00a0\u202f]/g, '')
      .replace(',', '.');
    const value = Number.parseFloat(normalised);
    return Number.isFinite(value) ? value : null;
  };

  // Le séparateur de sortie est celui que Dawn a écrit dans `data-max` : c'est la seule source
  // de vérité disponible côté client sur le format monétaire de la boutique.
  const separatorOf = (sample) => (String(sample).includes(',') ? ',' : '.');

  const format = (value, separator) => value.toFixed(DECIMALS).replace('.', separator);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class PriceSlider {
    constructor(root) {
      const inputs = root.querySelectorAll('input[type="text"], input[inputmode="decimal"]');
      if (inputs.length !== 2) return;

      this.root = root;
      this.minField = inputs[0];
      this.maxField = inputs[1];

      // `data-max` du champ « À » porte le plafond réel du filtre. Le champ « De » voit son
      // `data-max` réécrit par `PriceRange` de Dawn dès qu'une borne est saisie : le lire
      // ici donnerait un plafond mouvant.
      this.ceiling = toNumber(this.maxField.dataset.max) ?? toNumber(this.maxField.placeholder);
      if (!this.ceiling || this.ceiling <= 0) return;

      // Séparateur d'**affichage** seulement — il ne sert qu'à `aria-valuetext`, jamais à ce
      // qui part dans l'URL. Lu dans le `placeholder`, qui reste au format monétaire local,
      // et non dans `data-max`, désormais en décimale à point parce que c'est une borne
      // machine. Voir `snippets/price-facet.liquid`.
      this.separator = separatorOf(this.maxField.placeholder || '');
      this.step = this.ceiling > 100 ? 1 : 0.5;

      // Le symbole monétaire est lu dans le balisage de Dawn avant que le CSS ne le masque :
      // c'est la seule source côté client, et elle suit la devise de la boutique.
      this.symbol = this.root.querySelector('.field-currency')?.textContent.trim() || '';

      // `<legend>` absent de la branche prix : le nom du groupe est repris du `<summary>`,
      // pour que la poignée s'annonce « Prix, De » et non « De » toute seule.
      this.groupLabel = this.root.closest('.js-filter')?.querySelector('summary span')?.textContent.trim() || '';

      this.build();
      this.sync();
      this.bind();

      // Posé en dernier, et c'est le point qui rend l'amélioration progressive : le CSS ne
      // masque les champs de Dawn que sur un `price-range` portant ce drapeau. Toute sortie
      // anticipée du constructeur — deux champs introuvables, plafond inexploitable — laisse
      // donc le filtre dans son état Dawn, entièrement utilisable.
      root.dataset.enetSlider = 'true';
    }

    build() {
      // Le relevé remplace les deux champs par une seule ligne de valeurs. Elle est en
      // `aria-hidden` : chaque poignée annonce déjà sa propre valeur par `aria-valuetext`,
      // la relire ici la ferait entendre deux fois.
      this.readout = document.createElement('p');
      this.readout.className = 'enet-price-slider__readout';
      this.readout.setAttribute('aria-hidden', 'true');

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'enet-price-slider';

      this.minRange = this.createRange(this.minField, 'min');
      this.maxRange = this.createRange(this.maxField, 'max');

      this.wrapper.append(this.minRange, this.maxRange);
      this.root.append(this.readout, this.wrapper);
    }

    // LES CURSEURS SONT LE CONTRÔLE ACCESSIBLE DU FILTRE, ET PLUS UNE AFFORDANCE AU POINTEUR.
    //
    // Tant que les champs numériques étaient visibles, ils portaient l'accessibilité et les
    // curseurs étaient en `aria-hidden` hors tabulation, pour ne pas exposer quatre contrôles
    // pour un seul filtre. Les champs étant désormais masqués, le rôle se **déplace** : sans
    // ce transfert, le filtre de prix deviendrait inutilisable au clavier et muet aux lecteurs
    // d'écran. `<input type="range">` natif est fait pour ça — flèches, Origine et Fin,
    // valeur annoncée, sans une ligne de gestion clavier à écrire.
    createRange(field, role) {
      const range = document.createElement('input');
      range.type = 'range';
      range.min = '0';
      range.max = String(this.ceiling);
      range.step = String(this.step);
      range.className = `enet-price-slider__range enet-price-slider__range--${role}`;
      range.dataset.enetRangeRole = role;

      // Le nom vient du `<label>` que Dawn associe déjà au champ (« De », « À ») : il est
      // traduit, et le réutiliser évite d'inventer une clé pour un texte qui existe.
      const fieldLabel = this.root.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent.trim() || '';
      range.setAttribute('aria-label', [this.groupLabel, fieldLabel].filter(Boolean).join(' — '));

      return range;
    }

    // Champs → curseur. Appelé au démarrage et à chaque frappe dans un champ.
    sync() {
      const min = clamp(toNumber(this.minField.value) ?? 0, 0, this.ceiling);
      const max = clamp(toNumber(this.maxField.value) ?? this.ceiling, 0, this.ceiling);

      this.minRange.value = String(Math.min(min, max));
      this.maxRange.value = String(Math.max(min, max));
      this.paint();
    }

    // Position de la portion active, consommée par le CSS. Le dégradé est calculé ici et pas
    // en CSS pur : `calc()` ne sait pas lire la valeur courante d'un `input[type=range]`.
    // La même passe écrit la ligne de valeurs et les `aria-valuetext`, pour qu'affichage et
    // annonce ne puissent pas diverger.
    paint() {
      const min = Number(this.minRange.value);
      const max = Number(this.maxRange.value);

      this.wrapper.style.setProperty('--enet-range-start', `${(min / this.ceiling) * 100}%`);
      this.wrapper.style.setProperty('--enet-range-end', `${(max / this.ceiling) * 100}%`);

      const minText = this.money(min);
      const maxText = this.money(max);

      this.readout.textContent = `${minText} – ${maxText}`;

      // Sans `aria-valuetext`, un lecteur d'écran annonce le nombre brut — « 8 » au lieu de
      // « 8,00 € ». C'est la seule façon de rendre une devise sur un curseur natif.
      this.minRange.setAttribute('aria-valuetext', minText);
      this.maxRange.setAttribute('aria-valuetext', maxText);
    }

    // Espace insécable avant le symbole : c'est la règle typographique française, et le
    // symbole vient de la boutique, donc le format suit sa devise.
    money(value) {
      const amount = format(value, this.separator);
      return this.symbol ? `${amount}\u00a0${this.symbol}` : amount;
    }

    bind() {
      // Pendant le glissement : on ne fait que refléter la valeur dans le champ. Aucun
      // évènement n'est émis, donc aucune requête — sinon un glissement de bout en bout en
      // déclencherait une par image affichée, temporisation de Dawn ou pas.
      const onDrag = (event) => {
        const role = event.target.dataset.enetRangeRole;
        let min = Number(this.minRange.value);
        let max = Number(this.maxRange.value);

        // Les deux poignées ne se croisent pas : celle qu'on ne tient pas est poussée.
        if (role === 'min' && min > max) {
          max = min;
          this.maxRange.value = String(max);
        } else if (role === 'max' && max < min) {
          min = max;
          this.minRange.value = String(min);
        }

        // POINT ET NON VIRGULE : LA VALEUR DE CES CHAMPS PART TELLE QUELLE DANS L'URL.
        //
        // Ils portent `filter.v.price.gte` / `.lte`. Écrits au format monétaire local, ils
        // produisaient `filter.v.price.lte=8%2C50` et le filtre cessait de s'appliquer aux
        // résultats — sans rien casser à l'écran, la puce et le curseur affichant la borne.
        // Le séparateur local n'a plus qu'un usage : l'annonce vocale, dans `money()`.
        this.minField.value = min <= 0 ? '' : format(min, '.');
        this.maxField.value = max >= this.ceiling ? '' : format(max, '.');
        this.paint();
      };

      // Au relâchement — ou à chaque flèche du clavier si le curseur reçoit le focus par un
      // autre moyen : on rend la main à Dawn. `change` remet `PriceRange` d'aplomb (il
      // recalcule les bornes croisées des deux champs), `input` remonte au formulaire et
      // déclenche le rendu partiel.
      const onCommit = () => {
        [this.minField, this.maxField].forEach((field) => {
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.minField.dispatchEvent(new Event('input', { bubbles: true }));
      };

      [this.minRange, this.maxRange].forEach((range) => {
        range.addEventListener('input', onDrag);
        range.addEventListener('change', onCommit);
      });

      // Champ → curseur. Pas de `change` ici : la saisie au clavier doit déplacer la poignée
      // pendant la frappe, sans attendre la sortie du champ.
      [this.minField, this.maxField].forEach((field) => {
        field.addEventListener('input', () => this.sync());
      });
    }
  }

  // Dawn remplace le contenu de `.facets-container` à chaque filtrage : les `price-range`
  // rendus à nouveau n'ont plus de curseur. Un `MutationObserver` rattrape ces remplacements.
  //
  // La garde de double équipement est un `WeakSet` et **non** l'attribut `data-enet-slider` :
  // cet attribut a désormais un autre sens — « un curseur en état de marche est en place », il
  // est ce qui autorise le CSS à masquer les champs de Dawn. Le réutiliser comme marqueur de
  // passage aurait masqué les champs sur un élément dont la construction a échoué, c'est-à-dire
  // exactement le cas où il faut les garder. Un `WeakSet` ne laisse par ailleurs aucune trace
  // dans le DOM et libère les éléments que Dawn remplace.
  const seen = new WeakSet();

  const equipOne = (element) => {
    if (seen.has(element)) return;
    seen.add(element);
    new PriceSlider(element);
  };

  const equip = (root) => {
    root.querySelectorAll('price-range').forEach(equipOne);
  };

  const start = () => {
    equip(document);

    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches('price-range')) {
            equipOne(node);
          } else {
            equip(node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
